import useDebouncedValue from 'hooks/useDebouncedValue';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import { useParams } from 'react-router-dom';
import AppLoader from '../../../../Components/AppLoader';
import { ProjectServices } from '../../../../Services/ProjectServices';
import { notify } from '../../../../utils';
import { useStyle as onboardingStyle } from '../../../CustomerOnBoarding';
import {
    filterLicences,
    filterUsers,
    LICENCE_TYPES,
    projectUserCount,
    summarizeByType,
} from './assignmentModel';
import {
    applyAssignmentUpload,
    downloadAssignmentTemplate,
    fetchMatchingUserIds,
    formatBatchErrors,
    loadAssignmentMatrix,
    previewAssignmentUpload,
    saveAssignmentDiff,
} from './assignmentApi';
import useAssignmentDraft from './useAssignmentDraft';
import AssignmentMatrix from './AssignmentMatrix';
import AssignmentToolbar from './AssignmentToolbar';
import QuickAssignDialog from './QuickAssignDialog';
import CapacityCards from './CapacityCards';
import BulkAssignCard, { bulkActionState } from './BulkAssignCard';
import AttributeFilterBar from './AttributeFilterBar';
import {
    facetsFromUsers,
    filtersToParams,
    hasActiveFilters,
    normaliseFacets,
    userMatchesFilters,
    usersHaveAttributes,
} from './assignmentFilters';
import BulkAssignPreviewDialog from './BulkAssignPreviewDialog';

const projectServices = new ProjectServices();
const EMPTY_LIST = [];
const DEFAULT_ROWS_PER_PAGE = 50;

const useStyles = makeStyles(theme => ({
    assignmentIntro: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: '1px solid var(--dm-border, #dbe7f2)',
        borderRadius: 10,
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        [theme.breakpoints.down('sm')]: {
            alignItems: 'flex-start',
            flexDirection: 'column',
        },
    },
    introMain: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },
    introIcon: {
        width: 42,
        height: 42,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 11,
        color: '#ffffff',
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 8px 18px rgba(61, 125, 221, 0.22)',
    },
    introTitle: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    introText: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        lineHeight: 1.5,
        marginTop: 3,
    },
    projectUserCount: {
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.75, 1.25),
        border: '1px solid rgba(61, 125, 221, 0.22)',
        borderRadius: 999,
        color: '#1f4f91',
        backgroundColor: 'rgba(61, 125, 221, 0.1)',
        fontSize: '0.75rem',
        fontWeight: 700,
    },
    projectTabs: {
        width: '100%',
        maxWidth: '100%',
        marginBottom: theme.spacing(2),
        borderBottom: '1px solid #e1e7ee',
        '& .MuiTabs-scroller': {
            minWidth: 0,
        },
        '& .MuiTab-root': {
            minHeight: 48,
            padding: '8px 16px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#647184',
        },
        '& .Mui-selected': {
            color: theme.palette.primary.main,
        },
    },
    pendingBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1.5),
        border: '1px solid rgba(61, 125, 221, 0.3)',
            borderRadius: 8,
        backgroundColor: 'rgba(61, 125, 221, 0.08)',
        color: '#1f4f91',
            fontSize: '0.8125rem',
            fontWeight: 600,
    },
    pendingCounts: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
    },
    pendingAdd: { color: '#287a4b' },
    pendingRemove: { color: '#9b2c2c' },
    pendingActions: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    pendingButton: {
        textTransform: 'none',
        fontWeight: 700,
    },
    emptyState: {
        padding: theme.spacing(5, 2),
        textAlign: 'center',
        color: 'var(--dm-muted, #647184)',
        border: '1px dashed var(--dm-border, #d6dde6)',
        borderRadius: 8,
    },
    errorState: {
        padding: theme.spacing(4, 2),
        textAlign: 'center',
        color: '#9b2c2c',
        border: '1px dashed rgba(197, 48, 48, 0.4)',
        borderRadius: 8,
    },
}));

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const UnsavedChangesDialog = ({ open, message, saving, onCancel, onDiscard, onSave }) => (
    <Dialog open={open} onClose={saving ? undefined : onCancel} aria-labelledby="unsaved-changes-title">
        <DialogTitle id="unsaved-changes-title">Unsaved assignment changes</DialogTitle>
        <DialogContent>
            <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button onClick={onDiscard} disabled={saving}>Discard changes</Button>
            <Button variant="contained" color="primary" onClick={onSave} disabled={saving}>
                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save changes'}
            </Button>
        </DialogActions>
    </Dialog>
);

const AllocateLicence = ({
    activeStep,
    handleBack,
    handleNext,
    handleUpdateBackupFormData,
    onGoToStep,
}) => {
    const classes = useStyles();
    const onBorningClasses = onboardingStyle();
    const { id } = useParams();
    const customerId = id || localStorage.getItem('customerUUID');
    
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [matrix, setMatrix] = useState(null);
    const [matrixLoading, setMatrixLoading] = useState(false);
    const [matrixError, setMatrixError] = useState('');
    const [category, setCategory] = useState('all');
    const [userSearch, setUserSearch] = useState('');
    const [licenceSearch, setLicenceSearch] = useState('');
    const debouncedUserSearch = useDebouncedValue(userSearch);
    const debouncedLicenceSearch = useDebouncedValue(licenceSearch);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [quickOpen, setQuickOpen] = useState(false);
    const [quickInitialUserIds, setQuickInitialUserIds] = useState([]);
    const [serverFacets, setServerFacets] = useState({});
    const [attributeFilters, setAttributeFilters] = useState({});
    const [matchIds, setMatchIds] = useState(null);
    const [matching, setMatching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingProjectId, setPendingProjectId] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewFileName, setPreviewFileName] = useState('');
    const [applying, setApplying] = useState(false);
    const matrixRef = useRef(null);
    const loadToken = useRef(0);

    const licences = matrix?.licences || EMPTY_LIST;
    const users = matrix?.users || EMPTY_LIST;
    const {
        baseline,
        draft,
        diff,
        remainingByKey,
        isDirty,
        reset,
        rebase,
        discard,
        toggle,
        setColumnUsers,
        setRowLicences,
        setManyAssignments,
    } = useAssignmentDraft(licences);

    const loadMatrix = useCallback(async (projectId, intendedDraft) => {
        const token = loadToken.current + 1;
        loadToken.current = token;
        setMatrixLoading(true);
        setMatrixError('');
        try {
            const nextMatrix = await loadAssignmentMatrix(projectId);
            if (loadToken.current !== token) return null;
            matrixRef.current = nextMatrix;
            setMatrix(nextMatrix);
            if (intendedDraft) rebase(nextMatrix.baseline, intendedDraft);
            else reset(nextMatrix.baseline);
            return nextMatrix;
        } catch (error) {
            if (loadToken.current === token) {
                setMatrixError(error?.message || 'Assignments could not be loaded.');
            }
            return null;
        } finally {
            if (loadToken.current === token) setMatrixLoading(false);
        }
    }, [rebase, reset]);

    const refreshProjects = useCallback(async () => {
        const projectsRes = await projectServices.allProjectsByCustomer(customerId).catch(() => null);
        if (projectsRes?.results) setProjects(projectsRes.results);
    }, [customerId]);

    useEffect(() => {
        let cancelled = false;
        projectServices.allProjectsByCustomer(customerId)
            .then((projectsRes) => {
                if (cancelled) return;
                const rows = projectsRes?.results || [];
                setProjects(rows);
                const stored = localStorage.getItem('project_id');
                const initial = rows.find(row => row.project_uuid === stored) || rows[0];
                if (initial?.project_uuid) setSelectedProjectId(initial.project_uuid);
            })
            .catch(() => {
                if (!cancelled) notify('error', 'Projects could not be loaded.');
            })
            .finally(() => {
                if (!cancelled) setProjectsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [customerId]);

    useEffect(() => {
        if (!selectedProjectId) return;
        localStorage.setItem('project_id', selectedProjectId);
        setCategory('all');
        setUserSearch('');
        setLicenceSearch('');
        setPage(0);
        setMatrix(null);
        setAttributeFilters({});
        setServerFacets({});
        matrixRef.current = null;
        loadMatrix(selectedProjectId);
        let cancelled = false;
        projectServices.getUserFacets(selectedProjectId).then((response) => {
            if (!cancelled && response && response.status !== false) setServerFacets(normaliseFacets(response));
        });
        return () => { cancelled = true; };
    }, [selectedProjectId, loadMatrix]);

    useEffect(() => {
        setPage(0);
    }, [matchIds]);

    useEffect(() => {
        setPage(0);
    }, [debouncedUserSearch]);

    const visibleLicences = useMemo(
        () => filterLicences(licences, { type: category, search: debouncedLicenceSearch }),
        [licences, category, debouncedLicenceSearch],
    );
    const effectiveFacets = useMemo(
        () => (Object.keys(serverFacets).length ? serverFacets : facetsFromUsers(users)),
        [serverFacets, users],
    );
    const filtersActive = hasActiveFilters(attributeFilters);

    useEffect(() => {
        if (!filtersActive) {
            setMatchIds(null);
            return undefined;
        }
        if (usersHaveAttributes(users)) {
            setMatchIds(new Set(users.filter(user => userMatchesFilters(user, attributeFilters)).map(user => user.uuid)));
            return undefined;
        }
        let cancelled = false;
        setMatching(true);
        fetchMatchingUserIds(selectedProjectId, filtersToParams(attributeFilters))
            .then((ids) => { if (!cancelled) setMatchIds(new Set(ids)); })
            .catch(() => {
                if (!cancelled) {
                    setMatchIds(null);
                    notify('error', 'Matching users could not be loaded.');
                }
            })
            .finally(() => { if (!cancelled) setMatching(false); });
        return () => { cancelled = true; };
    }, [attributeFilters, filtersActive, users, selectedProjectId]);

    const filteredUsers = useMemo(() => {
        const searched = filterUsers(users, debouncedUserSearch);
        return matchIds ? searched.filter(user => matchIds.has(user.uuid)) : searched;
    }, [users, debouncedUserSearch, matchIds]);
    const filteredUserIds = useMemo(() => filteredUsers.map(user => user.uuid), [filteredUsers]);
    const pageUsers = useMemo(
        () => filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [filteredUsers, page, rowsPerPage],
    );
    const countsByType = useMemo(() => LICENCE_TYPES.reduce((items, { type }) => {
        items[type] = licences.filter(licence => licence.type === type).length;
        return items;
    }, {}), [licences]);
    const capacitySummary = useMemo(
        () => summarizeByType(licences, remainingByKey),
        [licences, remainingByKey],
    );

    const reportOutcome = useCallback(({ blocked = [] } = {}) => {
        if (!blocked.length) return;
        const text = blocked
            .map(item => `${item.name}: ${plural(item.skipped, 'user')} not assigned`)
            .join('; ');
        notify('warning', `Not enough licences left. ${text}.`);
    }, []);

    const handleToggle = useCallback((key, userId) => {
        reportOutcome(toggle(key, userId));
    }, [toggle, reportOutcome]);

    const handleSetColumn = useCallback((key, assign) => {
        reportOutcome(setColumnUsers(key, filteredUserIds, assign));
    }, [setColumnUsers, filteredUserIds, reportOutcome]);

    const handleSetRow = useCallback((userId, assign) => {
        reportOutcome(setRowLicences(userId, visibleLicences.map(licence => licence.key), assign));
    }, [setRowLicences, visibleLicences, reportOutcome]);

    const handleQuickApply = ({ keys, userIds, assign }) => {
        const outcome = setManyAssignments(keys, userIds, assign);
        if (outcome.applied) {
            notify('info', `${plural(outcome.applied, 'change')} added. Save to apply them.`);
        }
        reportOutcome(outcome);
        setQuickOpen(false);
    };

    const saveDraft = async () => {
        if (!isDirty) return true;
        const projectId = selectedProjectId;
        const intendedDraft = draft;
        setSaving(true);
        try {
            const result = await saveAssignmentDiff(projectId, diff, users);
            if (!result || result.status === false) {
                notify('error', formatBatchErrors(result));
                await loadMatrix(projectId, intendedDraft);
                return false;
            }
            notify('success', `Assignments saved: ${diff.addedCount} assigned, ${diff.removedCount} removed.`);
            await Promise.all([loadMatrix(projectId), refreshProjects()]);
            return true;
        } catch (error) {
            notify('error', error?.message || 'Assignments could not be saved.');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAndContinue = async () => {
        handleUpdateBackupFormData({ projectId: selectedProjectId }, 'allocateLicenses');
        const saved = await saveDraft();
        if (saved) handleNext();
    };

    const handleSelectProject = (event, nextProjectId) => {
        if (!nextProjectId || nextProjectId === selectedProjectId) return;
        if (isDirty) {
            setPendingProjectId(nextProjectId);
            return;
        }
        setSelectedProjectId(nextProjectId);
    };

    const resolvePendingProject = async (action) => {
        const nextProjectId = pendingProjectId;
        if (action === 'save' && !(await saveDraft())) return;
        if (action === 'discard') discard();
        setPendingProjectId('');
        if (action !== 'cancel') setSelectedProjectId(nextProjectId);
    };

    const selectedProject = projects.find(project => project.project_uuid === selectedProjectId);
    const projectName = selectedProject?.project_name || 'this project';

    const handleDownloadTemplate = async (fileFormat = 'xlsx') => {
        const current = matrixRef.current;
        if (!current) return;
        setBulkBusy(fileFormat === 'csv' ? 'download-csv' : 'download');
        try {
            await downloadAssignmentTemplate({
                projectId: selectedProjectId,
                projectName: selectedProject?.project_name,
                users: current.users,
                licences: current.licences,
                baseline: current.baseline,
                fileFormat,
            });
            notify('success', isDirty
                ? 'Template downloaded. It shows saved assignments only; unsaved matrix changes are not included.'
                : 'Template downloaded.');
        } catch (error) {
            notify('error', 'The template could not be downloaded.');
        } finally {
            setBulkBusy(null);
        }
    };

    const runPreview = async (file, current) => {
        if (!current) return;
        setBulkBusy('upload');
        setPreviewFileName(file.name);
        try {
            const result = await previewAssignmentUpload({
                projectId: selectedProjectId,
                file,
                users: current.users,
                licences: current.licences,
                baseline: current.baseline,
            });
            setPreview(result);
        } catch (error) {
            setPreview({ error: 'The file could not be read. Upload the template as .xlsx, .xls or .csv.' });
        } finally {
            setBulkBusy(null);
        }
    };

    const handleFileSelected = (file) => {
        if (isDirty) {
            setPendingFile(file);
            return;
        }
        runPreview(file, matrixRef.current);
    };

    const resolvePendingFile = async (action) => {
        const file = pendingFile;
        if (action === 'cancel') {
            setPendingFile(null);
            return;
        }
        let current = matrixRef.current;
        if (action === 'save') {
            if (!(await saveDraft())) return;
            current = matrixRef.current;
        } else {
            discard();
        }
        setPendingFile(null);
        runPreview(file, current);
    };

    const handleApplyUpload = async () => {
        if (!preview) return;
        setApplying(true);
        try {
            const result = await applyAssignmentUpload({
                projectId: selectedProjectId,
                preview,
                users: matrixRef.current?.users || users,
            });
            if (!result || result.status === false) {
                notify('error', formatBatchErrors(result));
            } else {
                const added = preview.summary?.to_add || 0;
                const removed = preview.summary?.to_remove || 0;
                notify('success', `File applied: ${added} assigned, ${removed} removed.`);
                setPreview(null);
            }
        } catch (error) {
            notify('error', error?.message || 'The file could not be applied.');
        } finally {
            setApplying(false);
            loadMatrix(selectedProjectId);
            refreshProjects();
        }
    };

    const matrixEmptyMessage = !licences.length
        ? 'No licences are allocated to this project yet. Go back to Allocation to give it licences.'
        : !users.length
            ? 'No users have been added to this project yet. Go back to Users to add them.'
            : !visibleLicences.length
                ? 'No licences match the current category or search.'
                : !filteredUsers.length
                    ? 'No users match your search.'
                    : '';

    const busy = saving || applying || matrixLoading;
    const { canDownload, canUpload } = bulkActionState({ busy, userCount: users.length, licenceCount: licences.length });
    const quickAssignReason = !users.length
        ? 'Add users to this project on the Users step first.'
        : !licences.length
            ? 'Allocate licences to this project on the Allocation step first.'
            : '';
    const goToAllocation = onGoToStep ? () => onGoToStep('allocation', { projectId: selectedProjectId }) : undefined;
    const goToUsers = onGoToStep ? () => onGoToStep('users', { projectId: selectedProjectId }) : undefined;

    return (
        <>
            {projectsLoading ? (
                <AppLoader minHeight={280} />
            ) : !projects.length ? (
                <div className={classes.emptyState}>
                    No projects yet. Go back and create a project before assigning licences.
                </div>
            ) : (
                <>
                <Tabs
                    className={classes.projectTabs}
                        value={selectedProjectId || false}
                    onChange={handleSelectProject}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="primary"
                    aria-label="Projects"
                >
                    {projects.map(project => {
                        const count = projectUserCount(project, project.project_uuid === selectedProjectId ? matrix : null);
                        return (
                            <Tab
                                key={project.project_uuid}
                                value={project.project_uuid}
                                label={`${project.project_name || 'Untitled project'}${count === null ? '' : ` · ${plural(count, 'user')}`}`}
                            />
                        );
                    })}
                </Tabs>

                    {matrixLoading && !matrix ? (
                <AppLoader minHeight={280} />
                    ) : matrixError ? (
                        <div className={classes.errorState}>
                            {matrixError}
                            <div style={{ marginTop: 12 }}>
                                <Button variant="outlined" color="primary" onClick={() => loadMatrix(selectedProjectId)}>
                                    Try again
                                </Button>
                            </div>
                        </div>
                    ) : matrix && (
            <>
            <section className={classes.assignmentIntro} aria-labelledby="assignment-heading">
                <div className={classes.introMain}>
                    <span className={classes.introIcon} aria-hidden="true">
                        <AssignmentTurnedInOutlinedIcon />
                    </span>
                    <div>
                        <Typography component="h2" id="assignment-heading" className={classes.introTitle}>
                                            Assign licences for {projectName}
                        </Typography>
                        <Typography className={classes.introText}>
                                            Tick cells in the matrix, use Quick assign for many users and licences at once, or upload a file. Licences come from the project&apos;s allocation.
                        </Typography>
                    </div>
                </div>
                <span className={classes.projectUserCount}>
                    <PeopleOutlineIcon fontSize="small" />
                                    {plural(users.length, 'project user')}
                </span>
            </section>

                            <CapacityCards
                                summary={capacitySummary}
                                activeType={category}
                                onSelect={setCategory}
                            />

                            <BulkAssignCard
                                onDownload={handleDownloadTemplate}
                                onFileSelected={handleFileSelected}
                                busy={bulkBusy}
                                downloadDisabled={!canDownload}
                                uploadDisabled={!canUpload}
                                userCount={users.length}
                                licenceCount={licences.length}
                                onGoToAllocation={goToAllocation}
                                onGoToUsers={goToUsers}
                            />

                            <AssignmentToolbar
                                category={category}
                                onCategoryChange={setCategory}
                                countsByType={countsByType}
                                totalLicences={licences.length}
                                userSearch={userSearch}
                                onUserSearchChange={setUserSearch}
                                licenceSearch={licenceSearch}
                                onLicenceSearchChange={setLicenceSearch}
                                onQuickAssign={() => {
                                    setQuickInitialUserIds([]);
                                    setQuickOpen(true);
                                }}
                                disabled={busy || !licences.length || !users.length}
                                disabledReason={quickAssignReason}
                            />

                            <AttributeFilterBar
                                facets={effectiveFacets}
                                filters={attributeFilters}
                                onChange={setAttributeFilters}
                                matchCount={matchIds ? matchIds.size : users.length}
                                totalUsers={users.length}
                                matching={matching}
                                disabled={busy || !licences.length}
                                onAssignMatching={() => {
                                    setQuickInitialUserIds(matchIds ? [...matchIds] : []);
                                    setQuickOpen(true);
                                }}
                            />

                            {isDirty && (
                                <div className={classes.pendingBar} role="status">
                                    <span className={classes.pendingCounts}>
                                        <span>Unsaved changes:</span>
                                        <span className={classes.pendingAdd}>+{diff.addedCount} to assign</span>
                                        <span className={classes.pendingRemove}>-{diff.removedCount} to remove</span>
                        </span>
                                    <span className={classes.pendingActions}>
                                        <Button
                        size="small"
                                            className={classes.pendingButton}
                                            onClick={discard}
                                            disabled={busy}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            className={classes.pendingButton}
                                            onClick={saveDraft}
                                            disabled={busy}
                                        >
                                            {saving ? <CircularProgress size={16} color="inherit" /> : 'Save changes'}
                                        </Button>
                                    </span>
                        </div>
                            )}

                            <AssignmentMatrix
                                users={pageUsers}
                                filteredUserIds={filteredUserIds}
                                licences={visibleLicences}
                                baseline={baseline}
                                draft={draft}
                                remainingByKey={remainingByKey}
                                onToggle={handleToggle}
                                onSetColumn={handleSetColumn}
                                onSetRow={handleSetRow}
                                totalUsers={filteredUsers.length}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={(event, nextPage) => setPage(nextPage)}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                emptyMessage={matrixEmptyMessage}
                            />
            </>
            )}

            <div className={onBorningClasses.buttons}>
                    <div className={onBorningClasses.wrapper}>
                        {activeStep !== 0 && (
                            <Button
                                onClick={handleBack}
                                variant="outlined"
                                color="default"
                                    className={onBorningClasses.cancelBtn}
                                    disabled={saving}
                                >
                                Back
                            </Button>
                        )}
                        <Button
                                type="button"
                                onClick={handleSaveAndContinue}
                            variant="contained"
                            color="primary"
                                disabled={busy}
                            className={onBorningClasses.button}
                        >
                                {saving ? <CircularProgress size={18} color="inherit" /> : 'Save and continue'}
                        </Button>
                    </div>
            </div>
                </>
            )}

            <QuickAssignDialog
                open={quickOpen}
                onClose={() => setQuickOpen(false)}
                onApply={handleQuickApply}
                licences={licences}
                users={users}
                draft={draft}
                remainingByKey={remainingByKey}
                initialUserIds={quickInitialUserIds}
            />

            <UnsavedChangesDialog
                open={Boolean(pendingProjectId)}
                saving={saving}
                message={`You have ${plural(diff.addedCount + diff.removedCount, 'unsaved change')} for ${projectName}. Save them before switching project?`}
                onCancel={() => resolvePendingProject('cancel')}
                onDiscard={() => resolvePendingProject('discard')}
                onSave={() => resolvePendingProject('save')}
            />

            <UnsavedChangesDialog
                open={Boolean(pendingFile)}
                saving={saving}
                message={`You have ${plural(diff.addedCount + diff.removedCount, 'unsaved change')} in the matrix. The file is compared with saved assignments, so save or discard them first.`}
                onCancel={() => resolvePendingFile('cancel')}
                onDiscard={() => resolvePendingFile('discard')}
                onSave={() => resolvePendingFile('save')}
            />

            <BulkAssignPreviewDialog
                open={Boolean(preview)}
                fileName={previewFileName}
                preview={preview}
                applying={applying}
                onClose={() => setPreview(null)}
                onApply={handleApplyUpload}
            />
        </>
    );
};

export default AllocateLicence;
